import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  BellRing,
  CheckCircle2,
  Download,
  ExternalLink,
  MonitorSmartphone,
  Power,
  RefreshCw,
  Timer,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { ThemeToggle } from '~/app/ThemeToggle';
import {
  type Computer,
  currentComputer,
  megabytes,
  parseFeed,
  type Release,
} from '~/features/download/platform';
import { AppleLogo, WindowsLogo } from '~/features/download/PlatformLogos';
import { desktop, isDesktop } from '~/lib/desktop';
import { BrandGlyph } from '~/ui/BrandMark';
import { Pill } from '~/ui/Pill';

export const Route = createFileRoute('/download')({
  component: DownloadPage,
});

/** Where the desktop app's release (11deeds_Desktop, `pnpm release`) puts the installer and its feed. */
const WINDOWS_INSTALLER = '/desktop/11Deeds-Setup.exe';
const WINDOWS_FEED = '/desktop/latest.yml';
/** Opens the installed app (11deeds_Desktop, src/main/links.ts). */
const OPEN_APP = 'elevendeeds://open';

const BUTTON =
  'inline-flex h-11 items-center justify-center gap-2 rounded-control px-5 text-[15px] font-medium transition-colors duration-150';
const PRIMARY = `${BUTTON} bg-accent text-on-accent hover:bg-accent-ink`;
const SECONDARY = `${BUTTON} border border-line bg-paper text-ink hover:bg-paper-2`;

/**
 * The desktop app's download page, public, as Slack's and Zoom's are: the app
 * for the computer it is opened on comes first, then every app with its logo
 * (Windows ready, Mac coming soon). A click on Download starts the installer
 * and the page says what to do with it. Anyone who has the app can open it
 * from here; inside the app the page just says so.
 */
function DownloadPage() {
  const [computer] = useState(currentComputer);
  const release = useLatestRelease();
  const [started, setStarted] = useState(false);
  const inApp = isDesktop();

  return (
    <main className="min-h-dvh bg-paper text-ink">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <span
              aria-hidden
              className="grid size-9 place-items-center rounded-control bg-accent text-on-accent"
            >
              <BrandGlyph className="size-full" />
            </span>
            <span className="font-display text-lg font-semibold">11Deeds</span>
          </Link>
          <div className="flex items-center gap-1">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-control px-3 py-2 text-sm text-ink-2 hover:bg-paper-2 hover:text-ink"
            >
              <ArrowLeft size={15} aria-hidden />
              Back to 11Deeds
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-4 pt-12 pb-10 text-center sm:pt-16">
        <p className="text-sm font-medium text-accent">Desktop app</p>
        <h1 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">
          11Deeds for your computer
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-ink-2">
          Your day in its own window. It starts with the computer, keeps notifications coming with
          the window closed, and shows the running timer on top of other apps.
        </p>
        <div className="mt-8">
          {inApp ? (
            <InApp />
          ) : started ? (
            <Started />
          ) : (
            <Suggested computer={computer} release={release} onDownload={() => setStarted(true)} />
          )}
        </div>
      </section>

      {!inApp && (
        <section aria-labelledby="all-downloads" className="mx-auto max-w-3xl px-4 pb-12">
          <h2 id="all-downloads" className="mb-3 text-sm font-medium text-ink-3">
            All downloads
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <AppCard
              logo={<WindowsLogo className="size-7" />}
              name="Windows"
              needs="Windows 10 and 11, 64-bit"
              yours={computer === 'windows'}
              detail={
                release
                  ? `Version ${release.version}${release.size ? ` · ${megabytes(release.size)}` : ''}`
                  : 'Installer (.exe)'
              }
            >
              <a
                href={WINDOWS_INSTALLER}
                download
                onClick={() => setStarted(true)}
                className={`${PRIMARY} w-full`}
              >
                <Download size={17} aria-hidden />
                Download for Windows
              </a>
            </AppCard>
            <AppCard
              logo={<AppleLogo className="size-7" />}
              name="macOS"
              needs="Apple silicon and Intel Macs"
              yours={computer === 'mac'}
              badge={<Pill tone="warn">Coming soon</Pill>}
              detail="In the works"
            >
              <button
                type="button"
                disabled
                className={`${SECONDARY} w-full cursor-not-allowed opacity-60`}
              >
                <AppleLogo className="size-4" />
                Coming soon
              </button>
            </AppCard>
          </div>
        </section>
      )}

      <section aria-labelledby="why" className="border-t border-line bg-paper-2/50">
        <div className="mx-auto max-w-3xl px-4 py-12">
          <h2 id="why" className="text-center font-display text-xl font-semibold">
            What the app adds to the browser
          </h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            <Perk icon={<Power size={18} aria-hidden />} title="Starts with your computer">
              It opens on the clock in the morning, so the day starts on time.
            </Perk>
            <Perk icon={<BellRing size={18} aria-hidden />} title="Notifications, window closed">
              Chat and task alerts keep coming from the tray, with a dot on the taskbar.
            </Perk>
            <Perk icon={<Timer size={18} aria-hidden />} title="The timer stays in sight">
              The running timer floats on top of other apps while you work.
            </Perk>
            <Perk icon={<RefreshCw size={18} aria-hidden />} title="Updates itself">
              New versions download in the background and install when you quit.
            </Perk>
          </ul>
        </div>
      </section>

      {!inApp && (
        <section className="mx-auto flex max-w-3xl flex-col items-center gap-3 px-4 py-10 text-center">
          <a href={OPEN_APP} className={SECONDARY}>
            <ExternalLink size={17} aria-hidden />
            Already have it? Open 11Deeds
          </a>
          <p className="text-sm text-ink-2">
            Or keep going{' '}
            <Link to="/" className="text-accent hover:underline">
              in the browser
            </Link>
            .
          </p>
        </section>
      )}

      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-5xl flex-wrap gap-x-5 gap-y-2 px-4 py-6 text-sm text-ink-3">
          <Link to="/privacy" className="hover:text-ink hover:underline">
            Privacy Policy
          </Link>
          <Link to="/terms" className="hover:text-ink hover:underline">
            Terms of Service
          </Link>
        </div>
      </footer>
    </main>
  );
}

/** The app for this computer, first and largest, or what to do where there is none yet. */
function Suggested({
  computer,
  release,
  onDownload,
}: {
  computer: Computer;
  release: Release | null;
  onDownload: () => void;
}) {
  if (computer === 'windows') {
    return (
      <div className="flex flex-col items-center gap-3">
        <a
          href={WINDOWS_INSTALLER}
          download
          onClick={onDownload}
          className={`${PRIMARY} h-12 px-7 text-base`}
        >
          <WindowsLogo className="size-[18px]" />
          Download for Windows
        </a>
        <p className="text-sm text-ink-3">
          {release ? `Version ${release.version}` : 'The newest version'}
          {release?.size ? ` · ${megabytes(release.size)}` : ''} · Windows 10 and 11. Installs for
          you alone, no administrator rights.
        </p>
        <p className="max-w-md rounded-control bg-paper-2 px-4 py-2.5 text-sm text-ink-2">
          If Windows shows <b className="text-ink">Windows protected your PC</b>, choose{' '}
          <b className="text-ink">More info</b>, then <b className="text-ink">Run anyway</b>. It
          asks only the first time; updates install without it.
        </p>
      </div>
    );
  }
  if (computer === 'mac') {
    return (
      <Advice icon={<AppleLogo className="size-6" />} title="The Mac app is coming soon">
        We are finishing it for Apple silicon and Intel Macs. Until then, 11Deeds works in Safari or
        Chrome on your Mac.
        <Link to="/" className={`${SECONDARY} mt-4`}>
          Keep going in the browser
        </Link>
      </Advice>
    );
  }
  if (computer === 'phone') {
    return (
      <Advice icon={<MonitorSmartphone size={24} aria-hidden />} title="The app is for computers">
        On a phone, 11Deeds works in the browser: add it to your home screen for notifications. To
        get the desktop app, open 11deeds.com/download on your Windows PC.
      </Advice>
    );
  }
  return (
    <Advice
      icon={<MonitorSmartphone size={24} aria-hidden />}
      title={computer === 'linux' ? 'No app for Linux yet' : 'Pick your computer below'}
    >
      {computer === 'linux'
        ? '11Deeds works in Chrome or Firefox on Linux. The Windows app, and the Mac app when it is ready, are below.'
        : 'The app is ready for Windows; the Mac app is coming soon.'}
    </Advice>
  );
}

/** After Download: what to do with the installer, as Slack and Zoom say it. */
function Started() {
  return (
    <div
      role="status"
      className="mx-auto max-w-lg rounded-panel border border-line bg-paper p-6 text-left shadow-layer"
    >
      <p className="flex items-center gap-2 font-medium">
        <CheckCircle2 size={18} aria-hidden className="text-done" />
        Your download has started
      </p>
      <ol className="mt-4 flex flex-col gap-3 text-sm text-ink-2">
        <Step n={1}>
          Open <b className="text-ink">11Deeds-Setup.exe</b> from your browser's downloads.
        </Step>
        <Step n={2}>
          It installs in a few seconds, for you alone. If Windows shows{' '}
          <b className="text-ink">Windows protected your PC</b>, choose{' '}
          <b className="text-ink">More info</b>, then <b className="text-ink">Run anyway</b>. This
          is Windows not knowing the app yet, not a problem with it.
        </Step>
        <Step n={3}>11Deeds opens. Sign in, and from then on it starts with your computer.</Step>
      </ol>
      <p className="mt-5 text-sm text-ink-3">
        Download did not start?{' '}
        <a href={WINDOWS_INSTALLER} download className="text-accent hover:underline">
          Try again
        </a>
        .
      </p>
    </div>
  );
}

/** Inside the desktop app there is nothing to download. */
function InApp() {
  const version = desktop()?.version;
  return (
    <Advice icon={<CheckCircle2 size={24} aria-hidden />} title="You are using the desktop app">
      {version ? `Version ${version}. ` : ''}It updates itself.
    </Advice>
  );
}

function Advice({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center rounded-panel border border-line bg-paper-2/60 px-6 py-6">
      <span className="grid size-11 place-items-center rounded-full bg-paper text-ink">{icon}</span>
      <p className="mt-3 font-medium">{title}</p>
      <div className="mt-1 flex flex-col items-center text-sm text-ink-2">{children}</div>
    </div>
  );
}

/** One app in "All downloads": its logo and system, what it needs, and its button. */
function AppCard({
  logo,
  name,
  needs,
  detail,
  yours,
  badge,
  children,
}: {
  logo: ReactNode;
  name: string;
  needs: string;
  detail: string;
  yours: boolean;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={`relative flex flex-col gap-4 rounded-panel border bg-paper p-5 ${
        yours ? 'border-accent ring-1 ring-accent' : 'border-line'
      }`}
    >
      {yours && (
        <span className="absolute -top-2.5 right-4 rounded-full bg-accent px-2.5 py-0.5 text-[11px] font-medium text-on-accent">
          Recommended for your computer
        </span>
      )}
      <div className="flex items-start gap-3">
        <span className="grid size-12 shrink-0 place-items-center rounded-control bg-paper-2 text-ink">
          {logo}
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 font-medium">
            {name}
            {badge}
          </p>
          <p className="text-sm text-ink-2">{needs}</p>
          <p className="text-xs text-ink-3">{detail}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Perk({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <li className="flex gap-3 rounded-panel bg-paper p-4">
      <span className="grid size-9 shrink-0 place-items-center rounded-control bg-accent-soft text-accent-ink">
        {icon}
      </span>
      <span>
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-sm text-ink-2">{children}</span>
      </span>
    </li>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="grid size-6 shrink-0 place-items-center rounded-full bg-accent-soft font-mono text-xs text-accent-ink">
        {n}
      </span>
      <span className="pt-0.5">{children}</span>
    </li>
  );
}

/** The newest Windows release, from the update feed the installed apps read; null until known. */
function useLatestRelease(): Release | null {
  const [release, setRelease] = useState<Release | null>(null);
  useEffect(() => {
    let live = true;
    fetch(WINDOWS_FEED, { cache: 'no-store' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((text) => {
        if (live) setRelease(parseFeed(text));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return release;
}
